import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../utils/api';
import { setAuth } from '../utils/auth';
import { toast } from 'sonner';
import PublicHeader from '../components/PublicHeader';
import { useTranslation } from '../hooks/useTranslation';
import { ChevronRight, ChevronLeft, X } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { RegisterInternationalServices } from './register/RegisterInternationalServices';
import { RegisterMedicalInfo } from './register/RegisterMedicalInfo';
import { RegisterLocationInfo } from './register/RegisterLocationInfo';
import { RegisterDoctorAvailability } from './register/RegisterDoctorAvailability';
import { RegisterDoctorEducation } from './register/RegisterDoctorEducation';
import { RegisterDoctorProfessionalInfo } from './register/RegisterDoctorProfessionalInfo';
import { RegisterBasicInfo } from './register/RegisterBasicInfo';
export default function Register() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [hospitals, setHospitals] = useState([]);
  const [hospitalSearchOpen, setHospitalSearchOpen] = useState(false);
  const [processingOAuth, setProcessingOAuth] = useState(false);
  const [formData, setFormData] = useState({
    // Basic info
    email: '',
    password: '',
    full_name: '',
    role: 'patient',
    country: '',
    phone: '',
    
    // Hospital specific fields
    hospital_name: '',
    hospital_type: 'multi-specialty',
    address: '',
    city: '',
    state: '',
    pincode: '',
    website: '',
    established_year: '',
    registration_number: '',
    bed_count: '',
    icu_beds: '',
    doctors_count: '',
    specialties: [],
    treatments: [],
    facilities: [],
    accreditations: [],
    languages_spoken: [],
    visa_assistance: false,
    airport_pickup: false,
    interpreter_services: false,
    description: '',
    
    // Doctor specific fields
    specialization: '',
    sub_specializations: [],
    experience_years: '',
    qualifications: [],
    medical_school: '',
    certifications: [],
    hospital_affiliations: [],
    consultation_fee: '',
    consultation_timings: '',
    availability: [],
    emergency_available: false,
    languages: ['English'],
    awards: [],
    research_interests: [],
    about: ''
  });
  const [loading, setLoading] = useState(false);
  const [newAward, setNewAward] = useState('');
  const [newResearchInterest, setNewResearchInterest] = useState('');

  // Handle Google OAuth callback
  useEffect(() => {
    const processOAuthCallback = async () => {
      const hash = window.location.hash;
      if (hash && hash.includes('session_id=')) {
        setProcessingOAuth(true);
        const sessionId = hash.split('session_id=')[1]?.split('&')[0];
        
        if (sessionId) {
          try {
            window.history.replaceState(null, '', window.location.pathname);
            
            const { data } = await api.post('/auth/google/session', { session_id: sessionId });
            
            setAuth(data.user);
            toast.success(`Welcome, ${data.user.full_name}! Your account has been created.`);
            navigate('/patient/dashboard');
          } catch (error) {
            toast.error('Google sign-up failed. Please try again.');
            setProcessingOAuth(false);
          }
        }
      }
    };
    
    processOAuthCallback();
  }, [navigate]);

  // Fetch hospitals for doctor registration
  useEffect(() => {
    if (formData.role === 'doctor') {
      fetchHospitals();
    }
  }, [formData.role]);

  // Handle Google Sign-Up button click
  const handleGoogleSignUp = () => {
    const redirectUrl = window.location.origin + '/register';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const fetchHospitals = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/public/hospitals/names`);
      if (response.ok) {
        const data = await response.json();
        setHospitals(data.hospitals || []);
      }
    } catch (err) {
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ 
      ...formData, 
      [name]: type === 'checkbox' ? checked : value 
    });
  };

  const toggleArrayItem = (field, item) => {
    const currentItems = formData[field] || [];
    if (currentItems.includes(item)) {
      setFormData({ ...formData, [field]: currentItems.filter(i => i !== item) });
    } else {
      setFormData({ ...formData, [field]: [...currentItems, item] });
    }
  };

  const addToArray = (field, value, setter) => {
    if (value.trim()) {
      const currentItems = formData[field] || [];
      if (!currentItems.includes(value.trim())) {
        setFormData({ ...formData, [field]: [...currentItems, value.trim()] });
      }
      setter('');
    }
  };

  const removeFromArray = (field, index) => {
    const currentItems = [...(formData[field] || [])];
    currentItems.splice(index, 1);
    setFormData({ ...formData, [field]: currentItems });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Prepare data based on role
      const submitData = {
        email: formData.email,
        password: formData.password,
        full_name: formData.role === 'hospital' ? formData.hospital_name : formData.full_name,
        role: formData.role,
        country: formData.country,
        phone: formData.phone
      };

      // Add hospital-specific data
      if (formData.role === 'hospital') {
        submitData.hospital_profile = {
          name: formData.hospital_name,
          hospital_type: formData.hospital_type,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          pincode: formData.pincode,
          website: formData.website,
          established_year: formData.established_year,
          registration_number: formData.registration_number,
          bed_count: formData.bed_count,
          icu_beds: formData.icu_beds,
          doctors_count: formData.doctors_count,
          specialties: formData.specialties,
          treatments: formData.treatments,
          facilities: formData.facilities,
          accreditations: formData.accreditations,
          languages_spoken: formData.languages_spoken,
          visa_assistance: formData.visa_assistance,
          airport_pickup: formData.airport_pickup,
          interpreter_services: formData.interpreter_services,
          description: formData.description
        };
      }

      // Add doctor-specific data
      if (formData.role === 'doctor') {
        submitData.doctor_profile = {
          specialization: formData.specialization,
          sub_specializations: formData.sub_specializations,
          experience_years: parseInt(formData.experience_years) || 0,
          qualifications: formData.qualifications,
          medical_school: formData.medical_school,
          certifications: formData.certifications,
          hospital_affiliations: formData.hospital_affiliations,
          consultation_fee: parseFloat(formData.consultation_fee) || 0,
          consultation_timings: formData.consultation_timings,
          availability: formData.availability,
          emergency_available: formData.emergency_available,
          languages: formData.languages,
          awards: formData.awards,
          research_interests: formData.research_interests,
          about: formData.about,
          city: formData.city,
          state: formData.state
        };
      }

      const { data } = await api.post('/auth/register', submitData);

      // Email verification required: no token returned — send user to sign in after verifying
      if (data.email_verification_required) {
        toast.success('Account created! Please check your email to verify your account before signing in.');
        navigate('/login');
        return;
      }

      setAuth(data.user);
      toast.success('Registration successful!');
      
      // Redirect based on role
      const role = data.user.role;
      if (role === 'patient') navigate('/patient/dashboard');
      else if (role === 'doctor') navigate('/doctor/dashboard');
      else if (role === 'hospital') navigate('/hospital/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const isHospital = formData.role === 'hospital';
  const isDoctor = formData.role === 'doctor';
  const totalSteps = isHospital ? 4 : isDoctor ? 4 : 1;

  const canProceed = () => {
    if (step === 1) {
      if (isHospital) {
        return formData.email && formData.password && formData.hospital_name && formData.phone;
      }
      if (isDoctor) {
        return formData.email && formData.password && formData.full_name && formData.phone;
      }
      return formData.email && formData.password && formData.full_name;
    }
    if (step === 2) {
      if (isDoctor) {
        return formData.specialization && formData.experience_years;
      }
      return formData.address && formData.city && formData.state;
    }
    if (step === 3) {
      if (isDoctor) {
        return formData.qualifications.length > 0;
      }
      return formData.specialties.length > 0;
    }
    return true;
  };

  const renderTagSelector = (field, options, label, maxHeight = 'max-h-32') => (
    <div>
      <label className="block text-sm font-medium mb-2">{label}</label>
      <div className={`border rounded-lg p-3 ${maxHeight} overflow-y-auto bg-gray-50`}>
        <div className="flex flex-wrap gap-2">
          {options.map(option => (
            <Badge
              key={option}
              variant={formData[field]?.includes(option) ? 'default' : 'outline'}
              className={`cursor-pointer text-xs transition-colors ${
                formData[field]?.includes(option) 
                  ? 'bg-[hsl(var(--primary))] text-white' 
                  : 'hover:bg-gray-100'
              }`}
              onClick={() => toggleArrayItem(field, option)}
            >
              {option}
              {formData[field]?.includes(option) && <X className="w-3 h-3 ml-1" />}
            </Badge>
          ))}
        </div>
      </div>
      {formData[field]?.length > 0 && (
        <p className="text-xs text-gray-500 mt-1">{formData[field].length} selected</p>
      )}
    </div>
  );

  const renderBasicInfo = () => (
    <RegisterBasicInfo formData={formData} handleChange={handleChange} handleGoogleSignUp={handleGoogleSignUp} isDoctor={isDoctor} isHospital={isHospital} loading={loading} processingOAuth={processingOAuth} setFormData={setFormData} t={t} />
  );

  // Doctor Step 2: Professional Info
  const renderDoctorProfessionalInfo = () => (
    <RegisterDoctorProfessionalInfo formData={formData} handleChange={handleChange} renderTagSelector={renderTagSelector} />
  );

  // Doctor Step 3: Education & Qualifications
  const renderDoctorEducation = () => (
    <RegisterDoctorEducation formData={formData} handleChange={handleChange} hospitalSearchOpen={hospitalSearchOpen} hospitals={hospitals} removeFromArray={removeFromArray} renderTagSelector={renderTagSelector} setHospitalSearchOpen={setHospitalSearchOpen} toggleArrayItem={toggleArrayItem} />
  );

  // Doctor Step 4: Availability & Additional Info
  const renderDoctorAvailability = () => (
    <RegisterDoctorAvailability addToArray={addToArray} formData={formData} handleChange={handleChange} newAward={newAward} newResearchInterest={newResearchInterest} removeFromArray={removeFromArray} renderTagSelector={renderTagSelector} setNewAward={setNewAward} setNewResearchInterest={setNewResearchInterest} />
  );

  const renderLocationInfo = () => (
    <RegisterLocationInfo formData={formData} handleChange={handleChange} />
  );

  const renderMedicalInfo = () => (
    <RegisterMedicalInfo renderTagSelector={renderTagSelector} />
  );

  const renderInternationalServices = () => (
    <RegisterInternationalServices formData={formData} handleChange={handleChange} renderTagSelector={renderTagSelector} />
  );

  const renderStepContent = () => {
    if (!isHospital && !isDoctor) {
      return renderBasicInfo();
    }

    if (isDoctor) {
      switch (step) {
        case 1: return renderBasicInfo();
        case 2: return renderDoctorProfessionalInfo();
        case 3: return renderDoctorEducation();
        case 4: return renderDoctorAvailability();
        default: return renderBasicInfo();
      }
    }

    // Hospital
    switch (step) {
      case 1: return renderBasicInfo();
      case 2: return renderLocationInfo();
      case 3: return renderMedicalInfo();
      case 4: return renderInternationalServices();
      default: return renderBasicInfo();
    }
  };

  const getStepTitle = () => {
    if (isDoctor) {
      switch (step) {
        case 1: return 'Basic Information';
        case 2: return 'Professional Details';
        case 3: return 'Education & Affiliations';
        case 4: return 'Availability & More';
        default: return '';
      }
    }
    if (isHospital) {
      switch (step) {
        case 1: return 'Basic Information';
        case 2: return 'Location & Contact';
        case 3: return 'Medical Information';
        case 4: return 'International Services';
        default: return '';
      }
    }
    return '';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[hsl(193_95%_95%)] to-[hsl(210_40%_98%)]">
      <PublicHeader currentPage="register" />
      <div className="pt-28 pb-12 px-4 flex items-center justify-center">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="text-center mb-6">
              <h1 className="font-heading text-3xl font-bold text-[hsl(var(--foreground))]" data-testid="register-title">
                {t('register.title')}
              </h1>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">
                {(isHospital || isDoctor) ? (
                  <>Step {step} of {totalSteps}: {getStepTitle()}</>
                ) : t('register.subtitle')}
              </p>
              
              {/* Progress bar for multi-step registration */}
              {(isHospital || isDoctor) && (
                <div className="mt-4 flex gap-1">
                  {[1, 2, 3, 4].map(i => (
                    <div 
                      key={i}
                      className={`h-1.5 flex-1 rounded-full transition-colors ${
                        i <= step ? 'bg-[hsl(var(--primary))]' : 'bg-gray-200'
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {renderStepContent()}

              {/* Navigation buttons */}
              <div className="flex gap-3 pt-4">
                {(isHospital || isDoctor) && step > 1 && (
                  <button
                    type="button"
                    onClick={() => setStep(step - 1)}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg font-semibold border border-gray-300 hover:bg-gray-50 transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Back
                  </button>
                )}
                
                {(isHospital || isDoctor) && step < totalSteps ? (
                  <button
                    key="register-continue"
                    type="button"
                    onClick={(e) => { e.preventDefault(); setStep(step + 1); }}
                    disabled={!canProceed()}
                    data-testid="register-continue-button"
                    className="flex-1 flex items-center justify-center gap-2 bg-[hsl(var(--primary))] text-white py-2.5 rounded-lg font-semibold hover:opacity-90 disabled:opacity-50 transition-colors"
                  >
                    Continue
                    <ChevronRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    key="register-submit"
                    type="submit"
                    disabled={loading || !canProceed()}
                    className="flex-1 bg-[hsl(var(--primary))] text-white py-2.5 rounded-lg font-semibold hover:opacity-90 disabled:opacity-50 transition-colors"
                    data-testid="register-submit-button"
                  >
                    {loading ? t('register.creatingAccount') : t('register.createAccountButton')}
                  </button>
                )}
              </div>
            </form>

            <div className="mt-6 text-center text-sm">
              <span className="text-[hsl(var(--muted-foreground))]">{t('register.haveAccount')} </span>
              <Link to="/login" className="text-[hsl(var(--primary))] font-medium hover:underline" data-testid="login-link">
                {t('register.signIn')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
